import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import {
  HardshipApiPayload,
  HardshipApplication,
  HardshipClientDocument,
  HardshipRequiredDocsCardState,
} from './helpers/hardship.models';
import { buildQualifierViewModel, HardshipQualifierViewModel, QualifyingDocumentViewModel } from './helpers/hardship.utils';
import { HardshipActiveView } from './hardship-request-details/hardship-request-details.component';
import {
  BsApiService,
  DocumentUploadService,
  FileUploadService,
  GenericResponseCode,
  UploadUrlResponse,
} from '@brightside-web/desktop/data-access/core-services';
import { HardshipRequiredDocsModalService } from './hardship-required-docs-modal.service';
import { ServiceInventoryService, ServiceState } from '@brightside-web/desktop/data-access/service-inventory';
import { Router } from '@angular/router';

import { catchError, EMPTY, filter, range, take, tap } from 'rxjs';
import { concatMap, retry, timer } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class HardshipRequiredDocsService {
  private _fileUploadService = inject(FileUploadService);
  private _uploadService = inject(DocumentUploadService);
  private _modals = inject(HardshipRequiredDocsModalService);
  private _bsApi = inject(BsApiService);
  private _serviceInventory = inject(ServiceInventoryService);
  private _router = inject(Router);

  private readonly _application = signal<HardshipApplication | null>(null);
  readonly payload = this._application.asReadonly();

  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  private readonly _processing = signal(false);
  readonly processing = this._processing.asReadonly();

  // doc.id → generated PDF bytes
  private _failedUploads: Record<string, Uint8Array> = {};

  readonly canEditDocs = signal<boolean>(true);

  private readonly _qualifiersVm = signal<HardshipQualifierViewModel[] | null>(null);

  readonly qualifiersVm = computed(() => {
    const qualifiers = this._qualifiersVm();
    const state = this._serviceState();
    
    if (!qualifiers) return null;
    
    // When state is UPLOAD, filter out SUBMITTED documents
    if (state === ServiceState.UPLOAD) {
      return qualifiers.map(qualifier => ({
        ...qualifier,
        qualifyingDocuments: qualifier.qualifyingDocuments.filter(
          doc => doc.state !== HardshipRequiredDocsCardState.SUBMITTED
        )
      }));
    }
    
    return qualifiers;
  });

  private readonly _activeView = signal<HardshipActiveView | undefined>(undefined);
  readonly activeView = this._activeView.asReadonly();

  private readonly _activeRoute = signal<'additional' | 'documents'>('documents');
  readonly activeRoute = this._activeRoute.asReadonly();

  private readonly _serviceState = signal<ServiceState | null>(null);
  readonly serviceState = this._serviceState.asReadonly();

  private _setApplication(application: HardshipApplication) {
    this._application.set(application);
    this._qualifiersVm.set(buildQualifierViewModel(application));
  }

  readonly applicationId = computed(() => this._application()?.id ?? '');

  readonly uploadedDocumentTitles = computed(() => {
    const payload = this._application();
  
    if (!payload) return [];

    const titleMap = new Map<string, string>();
  
    for (const qualifier of payload.config.qualifiers) {
      for (const qDoc of qualifier.qualifyingDocuments) {
        titleMap.set(qDoc.key, qDoc.title);
      }
    }

    const seenKeys = new Set<string>();
    const titles: string[] = [];

    for (const clientDoc of payload.clientDocuments) {
      const key = clientDoc.key;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        titles.push(titleMap.get(key) ?? key);
      }
    }

    return titles;
  });

  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    this._serviceInventory.getService('hardship_application')
      .pipe(
        filter((service) => !!service),
        tap((service) => {
          this._serviceState.set(service.state);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  getHardshipDocuments() {
    this._loading.set(true);
    this._error.set(null);

    this._bsApi.get<HardshipApiPayload>('api-mobile', '/customer-program/hardship/application').subscribe({
      next: response => {
        if (response.result.code === GenericResponseCode.SERVICE_OK && response.payload.applications.length > 0) {
          const application = response.payload.applications[0];
          if (!application.clientDocuments) {
            application.clientDocuments = [];
          }
          this._setApplication(application);
        }
        this._loading.set(false);
      },
      error: e => {
        this._error.set('Failed to load hardship documents');
        this._loading.set(false);
      }
    });
  }

  startUpload(doc: QualifyingDocumentViewModel): void {
    if (!this.canEditDocs()) return;

    const cachedPdf = this._failedUploads[doc.id];

    this._updateDocStateById(doc.id, HardshipRequiredDocsCardState.UPLOADING);

    if (cachedPdf) {
      // Retry with existing PDF – no picker
      this._uploadDocument(doc, cachedPdf);
    } else {
      // Fresh upload – need to build PDF first
      this._openUploadFlowWithPicker(doc);
    }
  }

  private _openUploadFlowWithPicker(doc: QualifyingDocumentViewModel): void {
    const payload = this._application();
    if (!payload) {
      this.completeUploadError(doc);
      return;
    }

    this._modals.openUploadModal(
      async () => {
        try {
          const pdfBytes = await this._fileUploadService.selectFilesAndBuildPdf({
            accept: 'image/*,application/pdf',
            multiple: true,
          });

          if (!pdfBytes) {
            this._updateDocStateById(doc.id, HardshipRequiredDocsCardState.ADD);
            return;
          }

          this._uploadDocument(doc, pdfBytes);
        } catch (err) {
          console.error('Error building PDF', err);
          this.completeUploadError(doc);
        }
      },
      () => {
        this._updateDocStateById(doc.id, HardshipRequiredDocsCardState.ADD);
      }
    );
  }

  private _uploadDocument(doc: QualifyingDocumentViewModel, pdfBytes: Uint8Array): void {
    const payload = this._application();
    if (!payload) {
      this.completeUploadError(doc);
      return;
    }

    const applicationId = payload.id;
    const fileKey = doc.key;

    this._uploadService
      .uploadDocument(applicationId, fileKey, 'HARDSHIP_APPLICATION', pdfBytes)
      .pipe(
        catchError((err) => {
          console.error('Upload failed', err);
          this._failedUploads[doc.id] = pdfBytes;
          this.completeUploadError(doc);
          return EMPTY;
        })
      )
      .subscribe({
        next: (response) => {
          delete this._failedUploads[doc.id];
          this._handleUploadSuccess(doc.key, doc.id, response);
        },
      });
  }

  private _handleUploadSuccess(fileKey: string, cardId: string, uploadResponse: UploadUrlResponse) {
    const application = this._application();
    if (!application) return;

    const documentId = uploadResponse.document.documentId;
    const createdDate = new Date().toISOString();

    const newClientDoc: HardshipClientDocument = {
      documentId,
      key: fileKey,
      status: HardshipRequiredDocsCardState.UPLOADED,
      createdDate,
    };

    const updatedPayload: HardshipApplication = {
      ...application,
      clientDocuments: [...application.clientDocuments, newClientDoc],
    };

    this._setApplication(updatedPayload);
    
    // For "Other" documents, the view model is rebuilt correctly by _setApplication
    // and we shouldn't update by cardId because it might match the new ADD card.
    // Only update state for non-Other documents or if the cardId doesn't end with :new
    if (!cardId.endsWith(':new')) {
     this._updateDocStateById(cardId, HardshipRequiredDocsCardState.UPLOADED);
    }
  }

  private _updateDocStateById(id: string, state: HardshipRequiredDocsCardState): void {
    this._qualifiersVm.update((current) => {
      if (!current) return current;

      return current.map((qualifier) => ({
        ...qualifier,
        qualifyingDocuments: qualifier.qualifyingDocuments.map((doc) =>
          this._docMatchesId(doc, id) ? { ...doc, state } : doc
        ),
      }));
    });
  }

  /** Matches both qualifier-level ids and ALL_DOCS synthetic ids from allDocumentsVm */
  private _docMatchesId(doc: QualifyingDocumentViewModel, id: string): boolean {
    if (doc.id === id) return true;
    if (!id.startsWith('ALL_DOCS:')) return false;

    if (id === `ALL_DOCS:${doc.key}`) return true;
    if (id.startsWith('ALL_DOCS:OTHER:uploaded:')) {
      const documentId = id.replace('ALL_DOCS:OTHER:uploaded:', '');
      return doc.uploadedDocument?.documentId === documentId;
    }
    if (id === 'ALL_DOCS:OTHER:new') {
      return doc.isOther && doc.state === HardshipRequiredDocsCardState.ADD && !doc.uploadedDocument;
    }
    return false;
  }

  completeUploadError(doc: QualifyingDocumentViewModel): void {
    this._updateDocStateById(doc.id, HardshipRequiredDocsCardState.ERROR);
  }

  deleteDocument(doc: QualifyingDocumentViewModel): void {
    if (!this.canEditDocs()) return;
    this.openDeleteModal(doc);
  }

  openUploadReminderModal(doc: QualifyingDocumentViewModel): void {
    this._modals.openUploadReminderModal(
      () => {
      },
      () => {
        // keep file, just close
      }
    );
  }

  openDeleteModal(doc: QualifyingDocumentViewModel): void {
    this._modals.openDeleteModal(
      () => {
        this.handleDelete(doc);
      },
      () => {
        // just close
      }
    );
  }

  handleDelete(doc: QualifyingDocumentViewModel): void {
    if (!doc.uploadedDocument) {
      return;
    }
    const documentId = doc.uploadedDocument.documentId;
    
    this._uploadService.deleteDocument(documentId).subscribe({
      next: () => {
        // Remove the document from all qualifiers by documentId
        this._removeDocumentFromAllQualifiers(documentId);
      },
      error: () => {
        this._modals.deleteRetry(
          () => {
            this.handleDelete(doc);
          },
          () => {}
        );
      },
    });
  }

  private _removeDocumentFromAllQualifiers(documentId: string): void {
  
    const application = this._application();
    if (!application) return;

    // Remove the document from clientDocuments by documentId
    const updatedClientDocuments = application.clientDocuments.filter(
      (clientDoc) => clientDoc.documentId !== documentId
    );

    const updatedApplication: HardshipApplication = {
      ...application,
      clientDocuments: updatedClientDocuments,
    };

    // Rebuild view models - this will automatically update all qualifiers
    // and set state to ADD for documents that no longer have uploadedDocument
    this._setApplication(updatedApplication);
  }

  readonly allDocumentsVm = computed<QualifyingDocumentViewModel[]>(() => {
    const qualifiers = this._qualifiersVm();
    const state = this._serviceState();
    
    if (!qualifiers) return [];

    const nonOtherByKey = new Map<string, QualifyingDocumentViewModel>();
    const otherUploadedByDocId = new Map<string, QualifyingDocumentViewModel>();
    let otherAddTemplate: QualifyingDocumentViewModel | null = null;

    for (const qualifier of qualifiers) {
      for (const doc of qualifier.qualifyingDocuments) {
        // When state is UPLOAD, skip SUBMITTED documents
        if (state === ServiceState.UPLOAD && doc.state === HardshipRequiredDocsCardState.SUBMITTED) {
          continue;
        }

        if (doc.isOther) {
          if (doc.state === HardshipRequiredDocsCardState.UPLOADED && doc.uploadedDocument?.documentId) {
            const docId = doc.uploadedDocument.documentId;

            if (!otherUploadedByDocId.has(docId)) {
              otherUploadedByDocId.set(docId, {
                ...doc,
                id: `ALL_DOCS:OTHER:uploaded:${docId}`,
              });
            }
          }

          if (doc.state === HardshipRequiredDocsCardState.ADD && !otherAddTemplate) {
            otherAddTemplate = doc;
          }

          continue;
        }

        const existing = nonOtherByKey.get(doc.key);

        if (!existing) {
          nonOtherByKey.set(doc.key, {
            ...doc,
            id: `ALL_DOCS:${doc.key}`,
          });
        } else {
          const isExistingAdd = existing.state === HardshipRequiredDocsCardState.ADD;
          const isDocAdd = doc.state === HardshipRequiredDocsCardState.ADD;

          if (isExistingAdd && !isDocAdd) {
            nonOtherByKey.set(doc.key, {
              ...doc,
              id: `ALL_DOCS:${doc.key}`,
            });
          }
        }
      }
    }

    const result: QualifyingDocumentViewModel[] = [];
    result.push(...nonOtherByKey.values());
    result.push(...otherUploadedByDocId.values());

    if (otherAddTemplate) {
      const hasAnyUploaded = otherUploadedByDocId.size > 0;

      result.push({
        ...otherAddTemplate,
        id: `ALL_DOCS:OTHER:new`,
        state: HardshipRequiredDocsCardState.ADD,
        uploadedDocument: null,
        hasUploaded: hasAnyUploaded,
        isOther: true,
      });
    }

    return result;
  });

  readonly canReview = computed(() => {
    const qualifiers = this._qualifiersVm();
    const state = this._serviceState();
    
    if (!qualifiers) return false;
    if (qualifiers.length === 0) return false;

    // When state is UPLOAD, only require a single upload total (deduplicated across qualifiers)
    if (state === ServiceState.UPLOAD) {
      // Short-circuit: return true as soon as we find one uploaded document
      for (const qualifier of qualifiers) {
        for (const doc of qualifier.qualifyingDocuments) {
          // Skip SUBMITTED documents
          if (doc.state === HardshipRequiredDocsCardState.SUBMITTED) {
            continue;
          }
          
          // Check if document is uploaded - if so, we have our minimum requirement
          if (doc.hasUploaded || doc.state === HardshipRequiredDocsCardState.UPLOADED) {
            if (doc.uploadedDocument?.documentId) {
              return true; // Found at least one uploaded document
            }
          }
        }
      }
      
      return false;
    }

    // Normal flow: require requiredCount per qualifier
    return qualifiers.every((qualifier) => {
      const required = qualifier.requiredCount ?? 0;

      if (required <= 0) return true;

      // Count uploaded documents, but stop once we reach the required count
      let uploadedCount = 0;
      for (const doc of qualifier.qualifyingDocuments) {
        if (doc.hasUploaded || doc.state === HardshipRequiredDocsCardState.UPLOADED) {
          uploadedCount++;
          if (uploadedCount >= required) {
            return true;
          }
        }
      }

      return uploadedCount >= required;
    });
  });

  setActiveView(view: HardshipActiveView): void {
  
    this._activeView.set(view);
  }

  submitApplication(): void {
    const applicationId = this.applicationId();
    if (!applicationId) {
      return;
    }

    this._processing.set(true);

    this._bsApi
      .post('api-mobile', '/customer-program/hardship/application', { applicationId, submitted: true })
      .pipe(
        retry({
          count: 3,
          delay: 500,
        })
      )
      .subscribe({
        next: (response) => {
          if (response.result.code === GenericResponseCode.SERVICE_OK) {
            this._pollInventoryUntilHardshipReview();
          }
        },
        error: () => {
          this._processing.set(false);
        },
      });
  }

  private _pollInventoryUntilHardshipReview(): void {
    const hardshipKey = 'hardship_application';
    const maxAttempts = 10;
    const retryDelayMs = 1000;

    range(0, maxAttempts)
      .pipe(
        concatMap((i) =>
          i === 0
            ? this._serviceInventory.loadInventory()
            : timer(retryDelayMs).pipe(concatMap(() => this._serviceInventory.loadInventory()))
        ),
        filter(
          (result) =>
            result.success &&
            result.items.some((item) => item.key === hardshipKey && item.state === ServiceState.REVIEW)
        ),
        take(1),
        tap(() => {
          this._router.navigate(['home']).then(() => {
            setTimeout(() => {
              this._processing.set(false);
            }, 1000);
          });
        })
      )
      .subscribe({
        complete: () => {
          this._router.navigate(['home']).then(() => {
            setTimeout(() => {
              this._serviceInventory.updateServiceState('hardship_application', ServiceState.REVIEW);
              this._processing.set(false);
            }, 1000);
          });
        },
        error: () => this._processing.set(false),
      });
  }

}
